import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetLedgerByUserIdQuery, useCreateCashTransactionMutation, useDeleteTransactionMutation } from '../services/ledger/ledgerApi';
import { useLazySearchDownlineByUserIdQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';
import { toast } from 'react-toastify';

// Menu Icon Component
const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

function MasterTransactionsPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  
  // Helper function to format date to 12-hour format
  const formatDateTo12Hour = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');
    
    return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };
  
  // Fetch user ledger data
  const { data: response, isLoading, error, refetch, isFetching } = useGetLedgerByUserIdQuery({ userId });

  // Create cash transaction mutation
  const [createTransaction, { isLoading: isSubmitting }] = useCreateCashTransactionMutation();
  
  // Delete transaction mutation
  const [deleteTransaction, { isLoading: isDeleting }] = useDeleteTransactionMutation();

  // Search downline hook
  const [searchDownline, { data: searchResponse, isLoading: searching }] = useLazySearchDownlineByUserIdQuery();

  // Search state
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  
  // Dropdown state for actions menu
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    client: '',
    collection: 'CASH A/C',
    amount: '',
    paymentType: '',
    remark: ''
  });

  // Parse response data
  const username = response?.username || '';
  const userType = response?.userType || '';
  const fullname = response?.fullname || response?.fullName || '';
  const balanceStr = response?.balance || '';
  const transactionData = Array.isArray(response?.data) ? response.data : [];
  
  // Use API totals
  const dena = Number(response?.dena || 0);
  const lena = Number(response?.lena || 0);
  
  // Parse balance
  const balanceMatch = balanceStr.match(/([\d.]+)\s*\(\s*(\w+)\s*\)/);
  const balance = balanceMatch ? Number(balanceMatch[1]) : 0;
  const balanceType = balanceMatch ? balanceMatch[2] : '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update client field when data loads
  useEffect(() => {
    if (username) {
      setFormData(prev => ({
        ...prev,
        client: userId
      }));
      setSearchTerm(`${username} (${fullname})`);
    }
  }, [username, userType, userId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Update clients when search response changes
  useEffect(() => {
    if (searchResponse) {
      // Check if response has data object (single user) or data array
      if (searchResponse.data) {
        const userData = searchResponse.data;
        // If it's a single user object, wrap it in an array
        if (!Array.isArray(userData)) {
          setClients([userData]);
        } else {
          setClients(userData);
        }
      }
    }
  }, [searchResponse]);

  // Handle search input change with debounce
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for search
    const timeout = setTimeout(() => {
      if (value.trim()) {
        searchDownline({ userId: value.trim(), userType: 'Client' });
      } else {
        setClients([]);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.client) {
      toast.error('Please select a client');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.paymentType) {
      toast.error('Please select payment type');
      return;
    }
    try {
      // Always use current date and time (ignore selected date)
      const now = new Date();
      
      const transactionData = {
        userId: formData.client,
        amount: parseFloat(formData.amount),
        paymentType: formData.paymentType,
        collection: formData.collection,
        remark: formData.remark,
        date: now.toISOString()
      };

      const result = await createTransaction(transactionData).unwrap();
      
      if (result.success) {
        // toast.success('Transaction created successfully');
        // Refetch ledger data
        await refetch();
        // Reset form
        setFormData({
          client: '',
          collection: 'CASH A/C',
          date: new Date().toISOString().split('T')[0],
          amount: '',
          paymentType: '',
          remark: ''
        });
        setSearchTerm('');
      } else {
        // toast.error(result.message || 'Failed to create transaction');
      }
    } catch (error) {
      // toast.error(error?.data?.message || 'Failed to create transaction');
      console.error('Transaction error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle dropdown click
  const handleDropdownClick = (e, itemId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  // Handle delete action
  const handleDeleteTransaction = async (item) => {
    setActiveDropdown(null);
    
    try {
      const result = await deleteTransaction(item.transactionId).unwrap();
      
      if (result.success) {
        toast.success('Transaction deleted successfully');
        // Refetch ledger data to update the list
        await refetch();
      } else {
        toast.error(result.message || 'Failed to delete transaction');
      }
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete transaction');
      console.error('Delete error:', error);
    }
  };

  return (
    <div className="">
      <div className="bg-white rounded shadow">
        {/* Header */}
        <div className="bg-[#2c3e3e] text-white px-5 py-3 flex items-center justify-between">
          <h2 className="text-xl font-normal">{userType || 'User'} Transactions</h2>
          <button 
            onClick={() => navigate(-1)}
            className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600"
          >
            Back
          </button>
        </div>

        {/* Form Section */}
        <div className="p-5 bg-gray-50">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <Loading />
            </div>
          ) : error ? (
            <div className="p-4 text-red-500 text-center">
              {error.message || 'Failed to load data'}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* Client Search */}
              <div className="relative">
                <label className="block mb-1" style={{ color: '#535353', fontSize: '14px' }}>
                  <span className="text-red-500">*</span> Client
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setClientDropdownOpen(true)}
                  placeholder="Search client by ID or name..."
                  className="w-full border border-gray-300 px-3 py-2"
                />
                {clientDropdownOpen && searchTerm && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-auto">
                    {searching ? (
                      <div className="px-3 py-2 text-gray-500">Searching...</div>
                    ) : clients.length === 0 ? (
                      <div className="px-3 py-2 text-gray-500">No clients found</div>
                    ) : (
                      clients.map((client) => (
                        <div
                          key={client.userId || client._id}
                          onClick={() => {
                            const clientId = client.id || client._id;
                            setFormData({ ...formData, client: clientId });
                            setSearchTerm(`${client.username} (${client.fullname || client.fullName || ''})`);
                            setClientDropdownOpen(false);
                            // Navigate to ledger page with client ID
                            navigate(`/ledger/master-transactions/${clientId}`);
                          }}
                          className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        >
                          {client.username} ({client.userId})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Collection */}
              <div>
                <label className="block mb-1" style={{ color: '#535353', fontSize: '14px' }}>
                  <span className="text-red-500">*</span> Collection
                </label>
                <select
                  name="collection"
                  value={formData.collection}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 px-3 py-2"
                >
                  <option>CASH A/C</option>
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block mb-1" style={{ color: '#535353', fontSize: '14px' }}>
                  <span className="text-red-500">*</span> Amount
                </label>
                <input
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="Enter amount"
                  className="w-full border border-gray-300 px-3 py-2"
                />
              </div>

              {/* Payment Type */}
              <div>
                <label className="block mb-1" style={{ color: '#535353', fontSize: '14px' }}>
                  <span className="text-red-500">*</span> Payment Type
                </label>
                <select
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 px-3 py-2"
                >
                  <option value="">Payment Type</option>
                  <option value="PAYMENT - DIYA">PAYMENT - DIYA</option>
                  <option value="RECEIPT - LIYA">RECEIPT - LIYA</option>
                </select>
              </div>

              {/* Remark */}
              <div>
                <label className="block mb-1" style={{ color: '#535353', fontSize: '14px' }}>
                  <span className="text-red-500">*</span> Remark
                </label>
                <input
                  type="text"
                  name="remark"
                  value={formData.remark}
                  onChange={handleInputChange}
                  placeholder="Remarks"
                  className="w-full border border-gray-300 px-3 py-2"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-500 text-white px-8 py-2 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
          )}
        </div>

        {/* Summary Section */}
        {!isLoading && !error && (
          <>
        {isFetching ? (
          <div className="p-8 flex justify-center">
            <Loading />
          </div>
        ) : (
          <>
        <div className="px-5 py-4 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4 text-lg">
            <div>
              <span className="text-red-600" style={{ fontWeight: 400 }}>
                Dena : {dena}
              </span>
            </div>
            <div>
              <span className="text-green-600" style={{ fontWeight: 400 }}>
                Lena : {lena}
              </span>
            </div>
            <div>
              <span className={balanceType === 'Dena' ? 'text-red-600' : 'text-green-600'} style={{ fontWeight: 400 }}>
                Balance: {balance} ( {balanceType} )
              </span>
            </div>
            <button 
              onClick={() => navigate(`/ledger/deleted-lenden/${userId}`)}
              className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600 rounded"
            >
              Deleted
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto px-5 pb-5">
          <table className="ledger-table w-full border-collapse border border-gray-300">
            <thead className="bg-[#2c3e3e] text-white">
              <tr>
                <th className="p-2 border border-gray-600 text-left">#</th>
                <th className="p-2 border border-gray-600 text-left">Date</th>
                {/* <th className="p-2 border border-gray-600 text-left">Post Date</th> */}
                {/* <th className="p-2 border border-gray-600 text-left">Collection Name</th> */}
                <th className="p-2 border border-gray-600 text-left">Debit</th>
                <th className="p-2 border border-gray-600 text-left">Credit</th>
                <th className="p-2 border border-gray-600 text-left">Balance</th>
                <th className="p-2 border border-gray-600 text-left">Payment Type</th>
                <th className="p-2 border border-gray-600 text-left">Remark</th>
              </tr>
            </thead>
            <tbody>
              {transactionData.map((item, index) => {
                // Highlight Liya (received/green) & Diya (paid/red) entries
                const pt = `${item.paymentType || ''} ${item.collectionName || ''}`.toUpperCase();
                const rowType = pt.includes('LIYA') ? 'ledger-liya' : pt.includes('DIYA') ? 'ledger-diya' : '';

                const itemId = `${item.no}-${index}`;

                return (
                  <tr key={index} className={rowType}>
                    <td className="p-2 border border-gray-300 relative">
                      {item.isDeleted === false && (
                        <>
                          <button
                            onClick={(e) => handleDropdownClick(e, itemId)}
                            className="bg-blue-500 text-white p-1 rounded hover:bg-blue-600"
                          >
                            <MenuIcon />
                          </button>
                          {activeDropdown === itemId && (
                            <div
                              ref={dropdownRef}
                              className="absolute bg-white border border-gray-300 rounded shadow-lg z-50"
                              style={{
                                top: '70%',
                                left: '0',
                                minWidth: '120px',
                                marginTop: '4px'
                              }}
                            >
                              <ul className="py-1">
                                <li
                                  onClick={() => handleDeleteTransaction(item)}
                                  className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 text-red-600 font-semibold"
                                >
                                  Delete
                                </li>
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="p-2 border border-gray-300">{formatDateTo12Hour(item.date)}</td>
                    {/* <td className="p-2 border border-gray-300">{formatDateTo12Hour(item.postDate)}</td> */}
                    {/* <td className="p-2 border border-gray-300">{item.collectionName}</td> */}
                    <td className="p-2 border border-gray-300">{item.debit || 0}</td>
                    <td className="p-2 border border-gray-300">{item.credit || 0}</td>
                    <td className="p-2 border border-gray-300">{item.balance}</td>
                    <td className="p-2 border border-gray-300">{item.paymentType}</td>
                    <td className="p-2 border border-gray-300">{item.remark}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default MasterTransactionsPage;
